/**
 * Drive Selector Component
 *
 * Provides a searchable/filterable drive selector with database lookup.
 * Displays drive specifications when selected.
 *
 * Supports both compact mode (for CriticalControls) and full mode.
 */

import { useState, useMemo } from 'react';
import {
  Autocomplete,
  TextField,
  Chip,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DRIVE_DATABASE } from '../../data/driveDatabase';
import { PRESETS_FIXED as PRESETS } from '../../data/presets';
import type { DriveSpec, DriveParameters } from '../../types';

interface DriveSelectorProps {
  value: DriveParameters;
  onChange: (value: DriveParameters) => void;
  compact?: boolean;
  showPresetOnly?: boolean;
}

interface DriveOption extends DriveSpec {
  label: string;
}

export function DriveSelector({
  value,
  onChange,
  compact = false,
  showPresetOnly = false,
}: DriveSelectorProps) {
  const [selectedDrive, setSelectedDrive] = useState<DriveOption | null>(null);

  const driveOptions = useMemo(() => {
    return DRIVE_DATABASE.map(drive => ({
      ...drive,
      label: compact
        ? `${drive.capacityTB}TB ${drive.manufacturer} ${drive.model}`
        : `${drive.manufacturer} ${drive.model} (${drive.capacityTB}TB ${drive.interface})`,
    }));
  }, [compact]);

  const handleSelect = (drive: DriveOption | null) => {
    setSelectedDrive(drive);
    if (drive) {
      // Infer drive type from interface: SAS typically indicates enterprise drives
      const inferredDriveType: 'consumer' | 'enterprise' =
        drive.interface === 'SAS' ? 'enterprise' : 'consumer';

      onChange({
        ...value,
        driveSizeTB: drive.capacityTB,
        wattsIdle: drive.wattsIdle,
        wattsLoad: drive.wattsLoad,
        driveIOPS: drive.iops,
        afr: drive.afr,
        peakSequentialMBps: drive.peakSequentialMBps,
        driveType: inferredDriveType,
      });
    }
  };

  const handlePresetChange = (presetId: string) => {
    const preset = PRESETS.find(p => p.id === presetId);
    if (preset) {
      onChange({
        ...value,
        driveSizeTB: preset.drive.driveSizeTB,
        wattsIdle: preset.drive.wattsIdle,
        wattsLoad: preset.drive.wattsLoad,
        driveIOPS: preset.drive.driveIOPS,
        afr: preset.drive.afr,
        peakSequentialMBps: preset.drive.peakSequentialMBps,
        driveType: preset.drive.driveType,
        driveCost: preset.drive.driveCost,
      });
    }
  };

  const stats = useMemo(() => {
    return {
      total: DRIVE_DATABASE.length,
      hdd: DRIVE_DATABASE.filter(d => d.type === 'HDD').length,
      ssd: DRIVE_DATABASE.filter(d => d.type === 'SSD').length,
    };
  }, []);

  // Compact mode with preset selector only
  if (compact) {
    // Find matching preset from current drive values
    const matchedPreset = PRESETS.find(p =>
      p.drive.driveSizeTB === value.driveSizeTB &&
      p.drive.wattsIdle === value.wattsIdle &&
      p.drive.wattsLoad === value.wattsLoad &&
      p.drive.driveIOPS === value.driveIOPS &&
      p.drive.afr === value.afr &&
      p.drive.peakSequentialMBps === value.peakSequentialMBps
    );

    return (
      <FormControl fullWidth size="small">
        <InputLabel>Drive Preset</InputLabel>
        <Select
          value={matchedPreset?.id || 'custom'}
          label="Drive Preset"
          onChange={(e) => {
            const selected = e.target.value;
            if (selected !== 'custom') {
              handlePresetChange(selected);
            }
          }}
        >
          <MenuItem value="custom">Custom</MenuItem>
          {PRESETS.map(preset => (
            <MenuItem key={preset.id} value={preset.id}>
              {preset.drive.driveSizeTB}TB - {preset.name.split(':')[0]}
            </MenuItem>
          ))}
        </Select>
        {matchedPreset && (
          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
            {matchedPreset.name}
          </Typography>
        )}
      </FormControl>
    );
  }

  // Full mode with database selector
  return (
    <Accordion defaultExpanded={!showPresetOnly} disableGutters>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          Drive Database Selection
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 1 }}>
        <Autocomplete
          options={driveOptions}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Select from Drive Database"
              placeholder="Search drives..."
              helperText={selectedDrive ? 'Drive selected - see specs below' : `${stats.total} drives available`}
            />
          )}
          onChange={(_e, value) => handleSelect(value)}
          isOptionEqualToValue={(option, value) => option.model === value.model && option.capacityTB === value.capacityTB}
          noOptionsText="No drives found"
          size="small"
        />

        {selectedDrive && (
          <Paper sx={{ mt: 2, p: 2, bgcolor: 'action.hover' }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Drive Specifications
            </Typography>
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none', width: '50%' }}>
                    Manufacturer
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.manufacturer}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    Model
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.model}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    Capacity
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.capacityTB} TB
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    Interface
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.interface}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    Type
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.type}
                    {selectedDrive.type === 'HDD' && selectedDrive.recordingType && (
                      <span> ({selectedDrive.recordingType})</span>
                    )}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    RPM
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.rpm === 0 ? 'N/A (SSD)' : `${selectedDrive.rpm} RPM`}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    IOPS
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.iops.toLocaleString()}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    Peak Speed
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.peakSequentialMBps} MB/s
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    Power (Idle/Load)
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.wattsIdle}W / {selectedDrive.wattsLoad}W
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" sx={{ borderBottom: 'none' }}>
                    AFR
                  </TableCell>
                  <TableCell sx={{ borderBottom: 'none' }}>
                    {selectedDrive.afr}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
              ℹ️ Pricing varies by region and retailer. Please enter your actual cost in the Custom Drive Parameters section below.
            </Typography>
          </Paper>
        )}

        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Chip label={`${stats.total} drives`} size="small" color="primary" variant="outlined" />
          <Chip label={`HDD: ${stats.hdd}`} size="small" variant="outlined" />
          <Chip label={`SSD: ${stats.ssd}`} size="small" variant="outlined" />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}
